/**
 * Date-of-birth helpers shared across the coupon registration + profile surfaces.
 *
 * Internally we store the raw digits (MMDDYYYY, up to 8) — the same "store
 * digits, format on the way out" approach as phone.ts.
 * - normalizeBirthdayDigits — strip to at most 8 digits.
 * - formatBirthdayDisplay   — render digits as "MM/DD/YYYY" (partial-friendly).
 * - isValidBirthday         — true when 8 digits form a real, non-future date (year >= 1900).
 * - toLLBirthdate           — "MM/DD/YYYY" for the Loyalty Lane API (empty/invalid in → empty out).
 * - parseLLBirthDate        — LL response (ISO, MM/DD/YYYY, or sentinel) → display digits.
 */

const MIN_YEAR = 1900;

/** Strip any input down to at most 8 digits (MMDDYYYY). */
export function normalizeBirthdayDigits(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\D/g, '').slice(0, 8);
}

/** Format digits as "MM/DD/YYYY", formatting progressively as the user types. */
export function formatBirthdayDisplay(value: string | number | null | undefined): string {
  const digits = normalizeBirthdayDigits(value);
  if (digits.length === 0) return '';
  if (digits.length < 3) return digits;
  if (digits.length < 5) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** Internal: are these exactly 8 digits a real, non-future date with year >= MIN_YEAR? */
function digitsAreValidDate(digits: string): boolean {
  if (digits.length !== 8) return false;
  const month = Number(digits.slice(0, 2));
  const day = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));

  if (year < MIN_YEAR) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  // Round-trip through a real Date to reject e.g. 02/30 or Feb 29 in a non-leap year.
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return false;
  }

  // Reject future dates (today is allowed).
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return date.getTime() <= today.getTime();
}

/** True when the value is a complete, real, non-future date of birth. */
export function isValidBirthday(value: string | number | null | undefined): boolean {
  return digitsAreValidDate(normalizeBirthdayDigits(value));
}

/** Convert to "MM/DD/YYYY" for the Loyalty Lane API. Empty/invalid in → empty out. */
export function toLLBirthdate(value: string | number | null | undefined): string {
  const digits = normalizeBirthdayDigits(value);
  if (!digitsAreValidDate(digits)) return '';
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * Parse whatever LL returns into display digits (MMDDYYYY).
 * Handles ISO "YYYY-MM-DD[THH:MM:SS]", US "MM/DD/YYYY", and sentinel/empty
 * values (returns "" so the UI shows "Not set").
 */
export function parseLLBirthDate(value: string | null | undefined): string {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';

  let month = '';
  let day = '';
  let year = '';

  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    [, year, month, day] = iso;
  } else {
    const us = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (!us) return '';
    month = us[1].padStart(2, '0');
    day = us[2].padStart(2, '0');
    year = us[3];
  }

  const digits = `${month}${day}${year}`;
  return digitsAreValidDate(digits) ? digits : '';
}
